/**
 * SEOSettings
 *
 * Collapsible panel for editing per-page SEO metadata:
 * - Page Title
 * - Meta Description (with character counter)
 * - Keywords (comma-separated)
 * - Slug (read-only)
 *
 * Sits above the SectionList in the "Page Info" tab.
 */

import { useState, useEffect, useCallback } from 'react';
import { Globe, ChevronDown, ChevronRight, Save, X, Pencil } from 'lucide-react';
import { updatePageSeo } from '../../api/editor';

// =============================================================================
// TYPES
// =============================================================================

interface SEOSettingsProps {
  websiteId: string;
  activePage: string;
  pageTitle: string;
  pageDescription: string;
  pageKeywords: string;
  pageSlug: string;
  onSeoChanged?: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get the color class for the meta description character counter.
 */
function getDescCountColor(count: number): string {
  if (count <= 140) return 'text-gray-400';
  if (count <= 155) return 'text-green-600';
  if (count <= 165) return 'text-amber-500';
  return 'text-red-500';
}

// =============================================================================
// COMPONENT
// =============================================================================

export function SEOSettings({
  websiteId,
  activePage,
  pageTitle,
  pageDescription,
  pageKeywords,
  pageSlug,
  onSeoChanged,
}: SEOSettingsProps) {
  // Panel state
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState(pageTitle);
  const [description, setDescription] = useState(pageDescription);
  const [keywords, setKeywords] = useState(pageKeywords);

  // Reset form when page changes or props update
  useEffect(() => {
    setTitle(pageTitle);
    setDescription(pageDescription);
    setKeywords(pageKeywords);
    setIsEditing(false);
  }, [activePage, pageTitle, pageDescription, pageKeywords]);

  const handleCancel = useCallback(() => {
    setTitle(pageTitle);
    setDescription(pageDescription);
    setKeywords(pageKeywords);
    setIsEditing(false);
  }, [pageTitle, pageDescription, pageKeywords]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      // Build updates — only include changed fields
      const updates: { pageSlug: string; title?: string; description?: string; keywords?: string } = {
        pageSlug: activePage,
      };

      if (title !== pageTitle) updates.title = title;
      if (description !== pageDescription) updates.description = description;
      if (keywords !== pageKeywords) updates.keywords = keywords;

      // Check if anything actually changed
      if (!updates.title && !updates.description && !updates.keywords) {
        setIsEditing(false);
        return;
      }

      const result = await updatePageSeo(websiteId, updates);
      if (result.success) {
        setIsEditing(false);
        onSeoChanged?.();
      }
    } catch (err) {
      console.error('[SEO] Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  }, [websiteId, activePage, title, description, keywords, pageTitle, pageDescription, pageKeywords, onSeoChanged]);

  const descLength = description.length;

  return (
    <div className="border-b border-gray-200 bg-white shrink-0">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        )}
        <Globe className="w-3.5 h-3.5 text-gray-500 shrink-0" />
        <span className="text-sm font-medium text-gray-700">SEO Settings</span>
        {/* Show a subtle indicator if SEO data exists */}
        {(pageTitle || pageDescription || pageKeywords) && (
          <span className="ml-auto text-xs text-gray-400">
            {pageKeywords ? 'configured' : 'basic'}
          </span>
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-3">
          {/* Edit/Save/Cancel buttons */}
          <div className="flex justify-end gap-1.5">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 rounded transition-colors"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                >
                  <Save className="w-3 h-3" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 rounded transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            )}
          </div>

          {/* Page Title */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Page Title
            </label>
            {isEditing ? (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Page title for browser tab and search results"
              />
            ) : (
              <p className="text-sm text-gray-900 truncate">
                {pageTitle || <span className="text-gray-400 italic">No title set</span>}
              </p>
            )}
          </div>

          {/* Meta Description */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Meta Description
            </label>
            {isEditing ? (
              <>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Brief description for search engine results (~155 chars)"
                />
                <p className={`text-xs mt-0.5 ${getDescCountColor(descLength)}`}>
                  {descLength}/155 characters
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-900 line-clamp-2">
                {pageDescription || <span className="text-gray-400 italic">No description set</span>}
              </p>
            )}
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Keywords
            </label>
            {isEditing ? (
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="seo, keywords, comma-separated"
              />
            ) : (
              <p className="text-sm text-gray-900">
                {pageKeywords ? (
                  <span className="flex flex-wrap gap-1">
                    {pageKeywords.split(',').map((kw, i) => (
                      <span
                        key={i}
                        className="inline-block px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                      >
                        {kw.trim()}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="text-gray-400 italic">No keywords set</span>
                )}
              </p>
            )}
          </div>

          {/* Slug (read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              URL Slug
            </label>
            <p className="text-sm text-gray-500 bg-gray-50 px-2.5 py-1.5 rounded-md font-mono">
              /{pageSlug}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
